import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Video, ArrowLeft, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const isMobileOrTablet = () => {
  const ua = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isIPad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  const isAndroidTablet = /Android/i.test(ua) && !/Mobile/i.test(ua);
  const isTouch = navigator.maxTouchPoints > 1;
  return isMobile || isIPad || isAndroidTablet || isTouch;
};

const ZoomEmbedded = () => {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const meetingNumber = searchParams.get('meeting') || '';
  const meetingPassword = searchParams.get('pwd') || '';
  const claseId = searchParams.get('clase_id') || '';
  const claseTitulo = searchParams.get('titulo') || 'Clase en Vivo';
  const originalZoomUrl = searchParams.get('zoom_url') || '';

  const [zoomOpen, setZoomOpen] = useState(false);
  const [popupRef, setPopupRef] = useState(null);
  const [isMobile] = useState(() => isMobileOrTablet());

  const userName = user ? `${user.nombre || ''} ${user.apellido || ''}`.trim() : 'Estudiante';
  const isHost = user?.rol === 'admin' || user?.rol === 'profesor';

  // ---- Registrar asistencia y notificar ----
  const registerAndNotify = useCallback(() => {
    const token = localStorage.getItem('token');
    if (claseId) {
      axios.post(`${API}/profesor/registrar-asistencia-clase`, {
        clase_id: claseId, clase_nombre: claseTitulo
      }, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(() => toast.success('Asistencia registrada'))
        .catch(() => {});
    }
    if (isHost) {
      axios.post(`${API}/zoom-sdk-test/start-recording`, { meeting_number: meetingNumber }).catch(() => {});
      axios.post(`${API}/zoom-sdk-test/host-joined`, { meeting_number: meetingNumber }).catch(() => {});
    }
  }, [claseId, claseTitulo, isHost, meetingNumber]);

  // ---- Entrar a clase ----
  const entrarAClase = useCallback(() => {
    registerAndNotify();

    const name = encodeURIComponent(userName);
    // Usar la URL ORIGINAL de Zoom (que el host creó con el passcode encriptado correcto)
    // y solo agregar uname. Esto evita problemas de reconstrucción de URL que hacen que
    // Zoom no reconozca el passcode y lo pida de nuevo.
    let url;
    if (originalZoomUrl) {
      const sep = originalZoomUrl.includes('?') ? '&' : '?';
      url = `${originalZoomUrl}${sep}uname=${name}`;
    } else {
      // Fallback: construir web client URL si no tenemos la original
      const pwd = encodeURIComponent(meetingPassword || '');
      url = `https://zoom.us/wc/${meetingNumber}/join?prefer=1&pwd=${pwd}&uname=${name}&lang=es-ES`;
    }

    if (isMobile) {
      // CELULAR/TABLET: Abrir en PESTAÑA NUEVA para que al cerrar Zoom
      // el usuario vuelva automáticamente a IBA Connect sin perder sesión.
      const newWin = window.open(url, '_blank');
      if (newWin) {
        toast.success('Zoom abierto en nueva pestaña. Cuando termines la clase, volvé a esta pestaña.');
      } else {
        // Si el navegador bloqueó el popup, fallback a misma pestaña
        localStorage.setItem('ibaconnect_return', window.location.href);
        window.location.href = url;
      }
    } else {
      // PC: Ventana popup más pequeña
      const sw = window.screen.width;
      const sh = window.screen.height;
      const w = Math.min(sw - 80, 1000);
      const h = Math.min(sh - 80, 650);
      const left = Math.round((sw - w) / 2);
      const top = Math.round((sh - h) / 2);

      const popup = window.open(
        url, 'zoom_clase',
        `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes`
      );
      setPopupRef(popup);
      setZoomOpen(true);

      const check = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(check);
          setZoomOpen(false);
          setPopupRef(null);
        }
      }, 2000);
    }
  }, [meetingNumber, meetingPassword, userName, isMobile, registerAndNotify, originalZoomUrl]);

  // ---- Host-left al salir ----
  useEffect(() => {
    const handleUnload = () => {
      if (isHost && meetingNumber) {
        navigator.sendBeacon(
          `${API}/zoom-sdk-test/host-left`,
          new Blob([JSON.stringify({ meeting_number: meetingNumber })], { type: 'application/json' })
        );
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (isHost && meetingNumber) {
        axios.post(`${API}/zoom-sdk-test/host-left`, { meeting_number: meetingNumber }).catch(() => {});
      }
    };
  }, [isHost, meetingNumber]);

  const goBack = useCallback(() => {
    if (popupRef && !popupRef.closed) popupRef.close();
    navigate(-1);
  }, [navigate, popupRef]);

  // ========== RENDER ==========
  return (
    <div className="fixed inset-0 z-50 bg-slate-950 flex items-center justify-center" data-testid="zoom-embedded">

      {/* PC: Zoom en popup activo */}
      {zoomOpen && (
        <div className="text-center space-y-5 p-6 max-w-sm">
          <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto">
            <Video className="h-8 w-8 text-green-400" />
          </div>
          <h2 className="text-xl font-bold text-white">{claseTitulo}</h2>
          <p className="text-green-400 font-medium">Clase en curso</p>
          <p className="text-slate-400 text-sm">Zoom está abierto en otra ventana.</p>
          <div className="flex gap-3 justify-center pt-2">
            <Button variant="outline" onClick={goBack} className="border-slate-600 text-slate-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver a IBA Connect
            </Button>
            <Button onClick={entrarAClase} className="bg-blue-600 hover:bg-blue-700">
              <ExternalLink className="w-4 h-4 mr-2" /> Reabrir Zoom
            </Button>
          </div>
        </div>
      )}

      {/* Pantalla principal (no zoom abierto) */}
      {!zoomOpen && (
        <div className="text-center space-y-5 p-6 max-w-sm">
          <div className="w-20 h-20 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto">
            <Video className="h-10 w-10 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{claseTitulo}</h2>
            <p className="text-slate-400 text-sm">
              Entrar como: <span className="text-white font-medium">{userName}</span>
            </p>
            {isHost && <Badge className="mt-2 bg-blue-600">Profesor / Host</Badge>}
          </div>

          {/* Botón principal para entrar a clase (sin espera del profesor) */}
          <Button onClick={entrarAClase}
            className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
            data-testid="zoom-join-btn">
            <Video className="w-5 h-5 mr-2" /> Entrar a Clase
          </Button>

          <div className="bg-red-500/20 border border-red-500 rounded-lg p-3">
            <p className="text-red-400 font-bold text-sm text-center">
              <i className="fas fa-circle text-red-500 animate-pulse mr-2"></i>
              Verificar que este grabando
            </p>
          </div>

          <Button variant="ghost" onClick={goBack} className="text-slate-500 hover:text-slate-300 text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Cancelar
          </Button>
        </div>
      )}
    </div>
  );
};

export default ZoomEmbedded;
